<script setup lang="ts">
import BlogBreadcrumb from '@/components/blog/BlogBreadcrumb.vue'
import BlogCard from '@/components/blog/BlogCard.vue'
import BlogFAQ from '@/components/blog/BlogFaq.vue'
import type { Blog } from '@/data/blogs'
import { formatDateToISO, getBlogBySlug, getBlogFolderByCategory } from '@/data/blogs'
import { blogs } from '@/data/blogs'
import { getLatestBlogs } from '@/data/blogs'
import { easeOut, motion } from 'motion-v'

const route = useRoute()

// Get the slug from the URL (e.g., "why-restaurant-marketing-feels-complicated")
const slugFromUrl = route.path.split('/').pop()

const lastThreeBlogs = getLatestBlogs(
  blogs.filter(blog => blog.slug !== slugFromUrl),
  3,
)

const blog = getBlogBySlug(slugFromUrl?.toString() || '')

const headlineVariants = {
  initial: { opacity: 0, y: '10%' },
  animate: { opacity: 1, y: 0, transition: { duration: 0.4, ease: easeOut } },
}

const paragraphVariants = {
  initial: { opacity: 0, y: 20 },
  animate: (custom: unknown) => {
    const i = Number(custom)
    return {
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.05, duration: 0.4, ease: easeOut },
    }
  },
}

const blogVariants = {
  initial: { opacity: 0, y: 50 },
  animate: (custom: unknown) => {
    const i = Number(custom)
    return {
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.1, duration: 0.5, ease: easeOut },
    }
  },
}

const blogSchema = {
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  'headline': blog?.seo?.title,
  'image': [
    blog?.image,
  ],
  'datePublished': formatDateToISO(blog?.seo?.datePublished || ''),
  // 'dateModified': blog?.seo?.dateModified,
  // 'author': blog?.seo?.author?.map(a => ({
  //   '@type': a['@type'],
  //   'name': a.name,
  //   'url': a.url,
  // })) || [],
}

const faqSchema = computed(() => {
  if (!blog?.faq?.length) return null

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': blog.faq.map(item => ({
      '@type': 'Question',
      'name': item.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': item.answer,
      },
    })),
  }
})

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  'itemListElement': [
    {
      '@type': 'ListItem',
      'position': 1,
      'name': 'Home',
      'item': `${import.meta.env.VITE_MONOLITH_URL}/`,
    },
    {
      '@type': 'ListItem',
      'position': 2,
      'name': 'Blog',
      'item': `${import.meta.env.VITE_MONOLITH_URL}/blog`,
    },
    {
      '@type': 'ListItem',
      'position': 3,
      'name': blog?.category,
      'item': `${import.meta.env.VITE_MONOLITH_URL}/blog?category=${encodeURIComponent(blog?.category || '')}`,
    },
    {
      '@type': 'ListItem',
      'position': 4,
      'name': blog?.title,
      'item': `${import.meta.env.VITE_MONOLITH_URL}/blog/${getBlogFolderByCategory(blog?.category || '')}/${slugFromUrl}`,
    },
  ],
}

useHead({
  link: [{
    rel: 'canonical',
    href: `${import.meta.env.VITE_MONOLITH_URL}/blog/${getBlogFolderByCategory(blog?.category || '')}/${slugFromUrl}`,
  }],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify(blogSchema), // BlogPosting
    },
    blog?.faq?.length
      ? {
          type: 'application/ld+json',
          innerHTML: JSON.stringify(faqSchema.value), // FAQPage
        }
      : null,
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify(breadcrumbSchema),
    },
  ].filter(Boolean),
})

useSeoMeta({
  title: blog?.seo?.title,
  description: blog?.seo?.description,
  ogTitle: blog?.seo?.title,
  ogDescription: blog?.seo?.description,
  ogImage: blog?.seo?.ogImage,
})
</script>
<template>
  <div class="isolate">
    <LayoutNavbar />

    <div
      class="mb-0 bg-gradient-to-b from-[#f8f8f0] from-50% to-[#f8f8f0] pt-4  md:shadow-2xl md:shadow-black/10"
    >
      <section class="mx-auto mb-8 max-w-screen-xl px-4 md:px-8 lg:max-w-screen-xl xl:px-8">
        <BlogBreadcrumb :blog="blog as Blog" />
        <motion.h1
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-3xl font-medium text-gray-700 lg:text-4xl"
        >
          {{ blog?.title }}
        </motion.h1>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-gray-500"
        >
          <router-link
            :to="{ path: '/blog', query: { category: blog?.category } }"
            class="text-[#FF7118] underline"
          >
            {{ blog?.category }}
          </router-link> • {{ blog?.date }}
        </motion.p>
        <motion.img
          :initial="{ opacity: 0, y: 50 }"
          :while-in-view="{ opacity: 1, y: 0, transition: { duration: 0.5, ease: easeOut } }"
          :src="blog?.image"
          :alt="blog?.alt"
          class="mt-6 w-full rounded-[2.5rem] object-cover drop-shadow-lg"
        />
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-700 lg:text-3xl"
        >
          30-Second Takeaways
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-gray-500"
        >
          Restaurant websites succeed when key information and next steps are clear, helping diners take action, like booking a reservation or placing an order.
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-gray-500"
        >
          <li><span>Getting the right visitors matters more than getting more traffic.</span></li>
          <li>
            <span>
              Clear, visible calls to action (CTAs) directly drive reservations, online orders, and catering inquiries.
            </span>
          </li>
          <li>
            <span>
              Diners decide fast—if key information isn’t obvious within seconds, they leave.
            </span>
          </li>
          <li>
            <span>
              Trust signals like reviews, photos, and updated menus influence decisions instantly.
            </span>
          </li>
          <li>
            <span>
              Fast mobile performance is critical. Slow websites lose customers.
            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Restaurant websites often get traffic but fail to convert because diners can’t quickly find what they need or take the next step.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Today, <a
            href="https://marketingltb.com/blog/statistics/restaurant-marketing-statistics/"
            target="_blank"
            class="text-[#FF7118] underline"
          >88% of diners search online before choosing where to eat</a>. But many restaurant websites act like digital brochures instead of conversion tools.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          When guests land on your site, they’re not browsing—they’re deciding.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          If your menu, hours, reservations, or ordering options aren’t immediately clear, they leave, and book somewhere else.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          In 2026, restaurant website design isn’t about aesthetics. It’s about speed, clarity, and making the next step effortless.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Because even small confusion can cost you tonight’s covers.
        </motion.p>
        <div class="mt-3 rounded-3xl border border-neutral-200 bg-white px-6 py-4">
          <motion.p
            :variants="paragraphVariants"
            :custom="0"
            initial="initial"
            while-in-view="animate"
            class="text-xl font-semibold leading-relaxed text-gray-800"
          >
            If your website isn’t converting the traffic you’re already getting, you’re likely leaving revenue on the table.
          </motion.p>
        </div>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-700 lg:text-3xl"
        >
          Why Do Restaurant Websites Get Traffic but Not Customers?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Restaurant websites get traffic but fail to convert because they don’t clearly guide diners to take action.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Most websites lose customers when:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-gray-500"
        >
          <li><span>Key information is hard to find</span></li>
          <li>
            <span>
              Calls to action are unclear or missing

            </span>
          </li>
          <li>
            <span>
              The site loads slowly on mobile

            </span>
          </li>
          <li>
            <span>
              Menus, hours, or photos are outdated

            </span>
          </li>
          <li>
            <span>
              The next step (reserve, order, inquire) isn’t obvious

            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Diners don’t spend time figuring things out. They choose the restaurant that makes the decision easiest.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-700 lg:text-3xl"
        >
          What Information Do Diners Expect on a Restaurant Website?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Diners visit restaurant websites to quickly find specific information that helps them decide where to eat.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Most diners look for:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-gray-500"
        >
          <li><span>Menu</span></li>
          <li>
            <span>
              Reservations or online ordering

            </span>
          </li>
          <li>
            <span>
              Hours of operation

            </span>
          </li>
          <li>
            <span>
              Location and directions

            </span>
          </li>
          <li>
            <span>
              Private dining or catering information
            </span>
          </li>
          <li>
            <span>
              Photos of food and atmosphere
            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          High-performing restaurant websites answer these questions immediately, without requiring scrolling or searching.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          If someone searches “private dining near me” and can’t find that page instantly, they don’t dig deeper. They leave.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          This is why aligning your website with your Google Business Profile optimization strategy is critical, so diners can move seamlessly from search to decision.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-700 lg:text-3xl"
        >
          Are Your Calls to Action Clear Within 5 Seconds?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Clear calls to action (CTAs) are one of the most important factors in restaurant website conversions.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          If diners can’t immediately see how to:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-gray-500"
        >
          <li>
            <span>Make a reservation
            </span>
          </li>
          <li>
            <span>Order online
            </span>
          </li>
          <li>
            <span>Submit a catering request
            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          —they leave.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          High-converting restaurant websites use:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-gray-500"
        >
          <li>
            <span>One primary CTA per page
            </span>
          </li>
          <li>
            <span>Buttons placed above the fold

            </span>
          </li>
          <li>
            <span>High-contrast, easy-to-click designs

            </span>
          </li>
          <li>
            <span>Repeated CTAs throughout the page

            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          The goal is simple: make the next step obvious and effortless.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-700 lg:text-3xl"
        >
          Does Your Website Build Trust Fast Enough?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          <RouterLink :to="{ name: '/how-it-works' }">
            <span class="text-[#FF7118] underline">Restaurant websites convert better</span>
          </RouterLink>  when they quickly build trust through visuals, reviews, and accurate information.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Diners trust websites that include:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-gray-500"
        >
          <li>
            <span>Recent, high-quality food photography
            </span>
          </li>
          <li>
            <span>
              Visible Google reviews and ratings

            </span>
          </li>
          <li>
            <span>
              Up-to-date menus and hours

            </span>
          </li>
          <li>
            <span>
              Clear contact and location information

            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Outdated content or missing information creates hesitation.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          And hesitation leads to lost customers.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-700 lg:text-3xl"
        >
          Is Your Restaurant Website Fast and Mobile-Friendly?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Mobile speed is critical because most diners search on their phones shortly before deciding where to eat.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          If your website:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-gray-500"
        >
          <li>
            <span>Loads slowly

            </span>
          </li>
          <li>
            <span>
              Feels cluttered

            </span>
          </li>
          <li>
            <span>
              Requires zooming

            </span>
          </li>
          <li>
            <span>
              Hides important information

            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Diners leave immediately.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          A high-performing mobile website should:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-gray-500"
        >
          <li>
            <span>Load in under 3 seconds

            </span>
          </li>
          <li>
            <span>
              Display menus and CTAs clearly

            </span>
          </li>
          <li>
            <span>
              Be easy to navigate with one hand

            </span>
          </li>
          <li>
            <span>
              Prioritize key information at the top

            </span>
          </li>
        </motion.ul>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-700 lg:text-3xl"
        >
          What Actually Drives Restaurant Website Conversions?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Restaurant website conversions happen when diners get clear answers, trust your business, and can take action instantly.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          <RouterLink :to="{ name: '/how-it-works' }">
            <span class="text-[#FF7118] underline">Websites convert more visitors</span>
          </RouterLink>  when they:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-gray-500"
        >
          <li>
            <span>Clearly display menu, hours, and location

            </span>
          </li>
          <li>
            <span>
              Show strong reviews and photos

            </span>
          </li>
          <li>
            <span>
              Load quickly on mobile devices

            </span>
          </li>
          <li>
            <span>
              Use simple, intuitive navigation

            </span>
          </li>
          <li>
            <span>
              Make the next step obvious (reserve, order, inquire)

            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          When these elements work together, traffic turns into paying customers.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          When they don’t, you lose business without even realizing it.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-700 lg:text-3xl"
        >
          How to Turn Restaurant Website Traffic Into Customers?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          To turn website visitors into customers, restaurants must focus on clarity, speed, and ease of action.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Follow these steps:
        </motion.p>
        <motion.ol
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          style="list-style: auto;"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside py-4 text-gray-500"
        >
          <li>
            <span>Make your menu, hours, and location immediately visible

            </span>
          </li>
          <li>
            <span>
              Add a clear primary CTA on every page

            </span>
          </li>
          <li>
            <span>
              Improve mobile speed and usability

            </span>
          </li>
          <li>
            <span>
              Showcase reviews and high-quality photos

            </span>
          </li>
          <li>
            <span>
              Keep all information updated and accurate

            </span>
          </li>
        </motion.ol>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Even small improvements can significantly increase reservations, orders, and inquiries.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-700 lg:text-3xl"
        >
          How Your Website Impacts Restaurant SEO and Local Search?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Your website plays a critical role in restaurant SEO because it helps Google determine your visibility in local search results.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          When your website is:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-gray-500"
        >
          <li>
            <span>Fast

            </span>
          </li>
          <li>
            <span>
              Mobile-friendly

            </span>
          </li>
          <li>
            <span>
              Clearly structured

            </span>
          </li>
          <li>
            <span>
              Aligned with your Google Business Profile

            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          —it becomes easier for Google to rank your restaurant.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Better rankings mean:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-gray-500"
        >
          <li>
            <span>More visibility

            </span>
          </li>
          <li>
            <span>
              More clicks

            </span>
          </li>
          <li>
            <span>
              More customers

            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Your website isn’t just a marketing tool. It’s a core part of your local search strategy.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-700 lg:text-3xl"
        >
          How Can You Turn More Website Visitors Into Guests?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          If your website isn’t converting the traffic you’re already getting, you’re likely leaving revenue on the table.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          <RouterLink :to="{ name: '/how-it-works' }">
            <span class="text-[#FF7118] underline">SpotHopper</span>
          </RouterLink>  combines:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-gray-500"
        >
          <li>
            <span>High-performing restaurant website design

            </span>
          </li>
          <li>
            <span>
              Built-in restaurant SEO

            </span>
          </li>
          <li>
            <span>
              Google Business Profile integration

            </span>
          </li>
          <li>
            <span>
              Automated review management

            </span>
          </li>
          <li>
            <span>
              AI-powered marketing tools

            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Designed to help restaurants get:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-gray-500"
        >
          <li>
            <span>More reservations

            </span>
          </li>
          <li>
            <span>
              More catering inquiries

            </span>
          </li>
          <li>
            <span>
              More online orders

            </span>
          </li>
          <li>
            <span>
              Better visibility in local search

            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          If you’re curious what higher conversion could look like, we’d be happy to show you. <RouterLink
            class="text-[#FF7118] underline"
            :to="{name:'/free-demo'}"
          >
            Book a quick SpotHopper demo
          </RouterLink> and see how it works behind the scenes.
        </motion.p>
      </section>

      <div class="mb-8 w-full bg-white  pb-8 pt-8 md:rounded-[5rem] ">
        <BlogFAQ :items="blog?.faq" />

        <section class="mx-auto mb-8 px-2 lg:max-w-screen-xl 2xl:px-0">
          <div class="grid w-full grid-cols-1 gap-x-6 gap-y-12 py-6 md:grid-cols-3">
            <motion.div
              v-for="(item, index) in lastThreeBlogs"
              :key="item.slug"
              :custom="index"
              :variants="blogVariants"
              initial="initial"
              while-in-view="animate"
            >
              <BlogCard v-bind="item" />
            </motion.div>
          </div>
        </section>
        <section class="mx-auto px-2 lg:max-w-screen-xl lg:pb-16 2xl:px-0">
          <div class="relative overflow-clip rounded-3xl px-4 py-10 md:px-8 md:py-20">
            <div class="absolute inset-0 isolate before:absolute before:inset-0 before:z-[1] before:bg-gray-800/70">
              <motion.img
                :initial="{ opacity: 0, y: 50 }"
                :while-in-view="{ opacity: 1, y: 0, transition: { duration: 0.5, ease: easeOut } }"
                :src="`${S3_ASSETS_URL}/home/cta-02.jpg`"
                class="aspect-video size-full object-cover object-center"
                role="presentation"
              />
            </div>

            <div class="isolate grid place-items-center gap-5">
              <motion.h3
                :variants="headlineVariants"
                initial="initial"
                while-in-view="animate"
                class="max-w-screen-sm text-balance text-center text-3xl leading-tight text-white lg:text-5xl"
              >
                Ready to take your restaurant marketing to the next level?
              </motion.h3>

              <UiButton
                class="group w-full max-w-xs lg:max-w-max"
                as-child
              >
                <RouterLink :to="{ name: '/free-demo' }">
                  Book a Free Demo

                  <UiIcon
                    name="arrow-forward-rounded"
                    class="transition-transform duration-200 group-hover:translate-x-1"
                  />
                </RouterLink>
              </UiButton>
            </div>
          </div>
        </section>
      </div>

      <LayoutFooter />
    </div>
  </div>
</template>
